/* eslint-disable vue/max-len,max-len */
import { createFetch } from '@vueuse/core';
import { computed } from 'vue';
import useErrorToasts from '@/composables/useErrorToasts';
import { API_BASE } from '@/config/appConfig';

import useAuthStore from '@/stores/auth';

// Adobe PSD conversions now live on the internal API (mono), using the
// session cookie and `Tgb-Owner` header rather than a separate lambda host.

/**
 * Attach the current team so the API can scope the request.
 * @param {Object} context - VueUse `beforeFetch` context.
 */
const adobeBeforeFetch = ({ options, cancel }) => {
  const authStore = useAuthStore();

  if (!authStore.currentTeamId) cancel();
  // eslint-disable-next-line no-param-reassign
  options.headers['Tgb-Owner'] = authStore.currentTeamId;
};

const adobeOnFetchError = async (payload) => {
  useErrorToasts(payload);
  return payload;
};

const adobeFetch = createFetch({
  baseUrl: API_BASE,
  combination: 'overwrite',
  fetchOptions: {
    credentials: 'include',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
    },
  },
});

/**
 * Run an Adobe request with the shared defaults. The owner header is always
 * applied (any caller-supplied `beforeFetch` is composed after it), while a
 * caller-supplied `onFetchError` replaces the default toast handler.
 * @param {String|Object} url - Request URL.
 * @param {Object} [callerOptions] - Per-call options.
 * @returns {Object} VueUse fetch instance.
 */
const adobeRequest = (url, callerOptions = {}) => adobeFetch(url, {
  ...callerOptions,
  beforeFetch: async (context) => {
    adobeBeforeFetch(context);

    if (callerOptions.beforeFetch) {
      return callerOptions.beforeFetch(context);
    }

    return undefined;
  },
  onFetchError: callerOptions.onFetchError || adobeOnFetchError,
});

export default () => ({
  getAdobeUploadLink: (userId, query, options = {}) => {
    const queryString = computed(() => Object.keys(query.value).map((k) => `${k}=${query.value[k]}`).join('&'));
    const url = computed(() => `adobe/psd/upload-url?${queryString.value}`);

    return adobeRequest(url, options).get().json();
  },
  getProcessStatus: (userId, options = {}) => {
    const url = 'adobe/psd/conversions';

    return adobeRequest(url, options).get().json();
  },
  makePusherAuth: (userId, payload, options = {}) => {
    const url = 'pusher/auth';

    return adobeRequest(url, options).post(payload).json();
  },
});
